import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared';
import {
  useFetchCanalVentaAll,
  useUpdateCanalVenta,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { CanalVenta } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';

const returnUrlCanalesVenta = ROUTER_PATHS.administration.canalVentaNav;

export interface CanalesVentaPageInterface {}

const CanalesVentaPage: React.FC<CanalesVentaPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_canalventa);

  const navigate = useNavigate();

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  // server side filters - colums table -----------------------
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* table -----------------------
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data -----------------------
  const {
    data: canalesVentaPagingRes,
    isLoading,
    isRefetching,
  } = useFetchCanalVentaAll({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      nombre: searchTerm,
      ...filterObject,
      filterByState: false,
    },
  });

  ///* mutations -----------------------
  const updateCanalVentaMutation = useUpdateCanalVenta(
    navigate,
    returnUrlCanalesVenta
  );

  ///* handlers -----------------------
  const onEdit = (original: CanalVenta) => {
    navigate(`${returnUrlCanalesVenta}/editar/${original.id_canal_venta}`);
  };

  ///* columns -----------------------
  const columns = useMemo<MRT_ColumnDef<CanalVenta>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
    /*   {
        accessorKey: 'codigo',
        header: 'Código',
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */

      // {
      //   accessorKey: 'supervisor',
      //   header: 'Supervisor',
      //   size: 300,
      //   Cell: ({ row }) => emptyCellOneLevel(row, 'supervisor'),
      // },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? 'Activo' : 'Inactivo'}
            setEstadoSwitch={speedState => {
              if (!hasPermission(PermissionsEnum.api_change_canalventa)) return;

              setConfirmDialog({
                isOpen: true,
                title: 'Cambiar Estado',
                subtitle:
                  '¿Está seguro que desea cambiar el estado de este registro ?',
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateCanalVentaMutation.mutate({
                    id: row.original.id_canal_venta!,
                    data: { estado: speedState as boolean } as any,
                  });
                },
              });
            }}
          />
        ),
      },

      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Canales de Venta"
      urlRedirect={`${returnUrlCanalesVenta}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_canalventa)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<CanalVenta>
        columns={columns}
        data={canalesVentaPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={canalesVentaPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasPermission(
          PermissionsEnum.api_change_canalventa
        )}
        enableEditing={hasPermission(PermissionsEnum.api_change_canalventa)}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default CanalesVentaPage;
